import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Rocket, Loader2, CheckCircle2, AlertCircle } from "lucide-react";

type State = "loading" | "valid" | "already" | "invalid" | "submitting" | "done" | "error";

export const Route = createFileRoute("/unsubscribe")({
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  head: () => ({
    meta: [
      { title: "Unsubscribe — Three Day Launch" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: UnsubscribePage,
});

function UnsubscribePage() {
  const { token } = useSearch({ from: "/unsubscribe" });
  const [state, setState] = useState<State>("loading");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}));
        if (!r.ok) { setState("invalid"); return; }
        if (data.valid) setState("valid");
        else if (data.reason === "already_unsubscribed") setState("already");
        else setState("invalid");
      })
      .catch(() => setState("invalid"));
  }, [token]);

  const handleConfirm = async () => {
    setState("submitting");
    try {
      const r = await fetch("/email/unsubscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data?.error || "Failed");
      if (data.success) setState("done");
      else if (data.reason === "already_unsubscribed") setState("already");
      else { setErrorMsg(data?.error || "Failed"); setState("error"); }
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : "Failed");
      setState("error");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="mx-auto max-w-4xl px-6 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary-gradient flex items-center justify-center">
              <Rocket className="h-4 w-4 text-white" />
            </div>
            <span className="font-bold">Three Day Launch<sup className="text-[0.6em] ml-0.5">®</sup></span>
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-md px-6 py-20">
        <div className="bg-card rounded-3xl border border-border shadow-card-soft p-8 text-center">
          {state === "loading" && (
            <><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /><p className="mt-4 text-muted-foreground">Verifying...</p></>
          )}
          {state === "valid" && (
            <>
              <h1 className="text-2xl font-bold">Unsubscribe?</h1>
              <p className="mt-3 text-muted-foreground">Click below to stop receiving emails from Three Day Launch.</p>
              <Button onClick={handleConfirm} className="mt-6 w-full" size="lg">Confirm unsubscribe</Button>
            </>
          )}
          {state === "submitting" && (
            <><Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" /><p className="mt-4 text-muted-foreground">Processing...</p></>
          )}
          {state === "done" && (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto text-primary" />
              <h1 className="text-2xl font-bold mt-4">You've unsubscribed</h1>
              <p className="mt-3 text-muted-foreground">You won't receive any more emails from us.</p>
            </>
          )}
          {state === "already" && (
            <>
              <CheckCircle2 className="h-12 w-12 mx-auto text-primary" />
              <h1 className="text-2xl font-bold mt-4">Already unsubscribed</h1>
              <p className="mt-3 text-muted-foreground">This email is already removed from our list.</p>
            </>
          )}
          {state === "invalid" && (
            <>
              <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
              <h1 className="text-2xl font-bold mt-4">Invalid link</h1>
              <p className="mt-3 text-muted-foreground">This unsubscribe link is invalid or has expired.</p>
            </>
          )}
          {state === "error" && (
            <>
              <AlertCircle className="h-12 w-12 mx-auto text-destructive" />
              <h1 className="text-2xl font-bold mt-4">Something went wrong</h1>
              <p className="mt-3 text-muted-foreground">{errorMsg}</p>
              <Button onClick={handleConfirm} variant="outline" className="mt-6">Try again</Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
